// OurApproach.js
import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Eye, Users, Headphones } from 'lucide-react';

const OurApproach = () => {
  const principles = [
    {
      icon: MessageCircle,
      title: 'Consultation Comes First',
      description: 'Nothing starts without a frank conversation. We look at eligibility, set expectations straight, and map out what is realistic before anyone commits to anything.',
      color: 'blue'
    },
    {
      icon: Eye,
      title: 'Clarity of Scope',
      description: 'Our processes are written down and explained out loud. You will know the limits of our role, including the things we cannot do, at every point.',
      color: 'green'
    },
    {
      icon: Users,
      title: 'Talking Directly',
      description: 'Once a university or an employer is in touch with you, that conversation is yours to hold. We stay on hand to interpret it, prepare you, and advise.',
      color: 'orange'
    },
    {
      icon: Headphones,
      title: 'Support That Continues',
      description: 'An offer letter or an interview date is not the end of our involvement. We keep explaining what happens next and how to be ready for it.',
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'border-t-blue-500',
      green: 'border-t-green-500',
      orange: 'border-t-orange-500',
      purple: 'border-t-purple-500'
    };
    return colors[color] || 'border-t-blue-500';
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Our Approach
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            A handful of principles shape how we deal with everyone who comes to us
          </p>
        </motion.div>

        {/* Principles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {principles.map((principle, index) => {
            const Icon = principle.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-white p-6 border-t-2 ${getColorClasses(principle.color)} hover:shadow-lg transition-shadow`}
              >
                <Icon size={32} className="text-gray-700 mb-4" />
                
                <h3 className="text-lg font-semibold text-primary mb-3 leading-tight">
                  {principle.title}
                </h3>
                
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {principle.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OurApproach;
